import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Plane } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS } from "@/data/content";

export default function Navbar({ onPlanTrip }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-white/85 backdrop-blur-xl border-b border-white/40 shadow-[0_4px_30px_rgba(10,35,66,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center group" data-testid="logo-link">
  <img
  src={
  location.pathname === "/"
    ? (scrolled ? "/Travwave-logo.png" : "/Travwave-logo-white-square.png")
    : "/Travwave-logo.png"
}
  alt="Travwave"
  className="h-44 w-auto object-contain"
  />
</Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative text-sm font-medium transition-colors ${
                  solid ? "text-[#1F2937] hover:text-[#C9A227]" : "text-white/90 hover:text-[#C9A227]"
                } ${active ? "!text-[#C9A227]" : ""}`}
              >
                {l.label}
                {active && <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-[#C9A227]" />}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="plan-trip-cta-nav"
            onClick={onPlanTrip}
            className="btn-gold hidden md:inline-flex text-sm"
          >
            Plan My Trip
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 rounded-full ${solid ? "text-[#0A2342]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-gray-100"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`mnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[#0A2342] font-medium py-3 border-b border-gray-100"
                >
                  {l.label}
                </Link>
              ))}
              <button
                data-testid="plan-trip-cta-mobile"
                onClick={() => { setOpen(false); onPlanTrip(); }}
                className="btn-gold w-full justify-center mt-3"
              >
                Plan My Trip
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
